import { MediaTypeEnum } from '@goldcast/api/content';
import { SidebarContext } from '../SideBar/SideBarUtils';
import DefaultPreview from './DefaultPreview';
import SpeakerGridTile from './SpeakerGridTile';
import ActiveSpeakerTile from './ActiveSpeakerTile';
import Audiogram from './Audiogram/Audiogram';
import { CLIP_UNAVAILABLE_MESSAGES, ClipLayoutStatus, DEFAULT_REASON } from './constants';
import type { Clip } from '@/player-config/src/types/clip-types';
import type { LayoutType } from '@/player-config/src/types/asset-types';

import { getLayoutAvailabilityStatus } from '@/context/ClipsContext/ClipsContextUtils';
import {
  isClipFromRecordingStudio,
  isLiveEventRemotionWithPreviewAndAlignedStream,
  isClipFromUploads,
  isClipPreRecordedEvent
} from '@/player-config/src/utils/clipUtils';

export interface MagicLayoutTileType {
  tileTitle: string;
  tileContent: (any) => React.ReactElement;
  tileContentProps?: Record<string, unknown>;
  sideBarContextOnSelect: SidebarContext;
  magicLayoutType: LayoutType;
  visible: boolean;
}

export const tiles: Record<MediaTypeEnum, MagicLayoutTileType[]> = {
  VIDEO: [
    {
      tileTitle: 'Default',
      tileContent: DefaultPreview,
      sideBarContextOnSelect: SidebarContext.Default,
      magicLayoutType: 'DEFAULT',
      visible: true
    },
    {
      tileTitle: 'Audiogram',
      tileContent: Audiogram,
      tileContentProps: { previewOnly: true, isMinimized: true },
      sideBarContextOnSelect: SidebarContext.Audiogram,
      magicLayoutType: 'AUDIOGRAM',
      visible: true
    },
    {
      tileTitle: 'Active Speaker',
      tileContent: ActiveSpeakerTile,
      sideBarContextOnSelect: SidebarContext.ActiveSpeaker,
      magicLayoutType: 'SPEAKER',
      visible: true
    },
    {
      tileTitle: 'Speaker Grid',
      tileContent: SpeakerGridTile,
      sideBarContextOnSelect: SidebarContext.SpeakerGrid,
      magicLayoutType: 'GRID',
      visible: true
    }
  ],
  AUDIO: [
    {
      tileTitle: 'Audiogram',
      tileContent: Audiogram,
      tileContentProps: { previewOnly: true, isMinimized: true },
      sideBarContextOnSelect: SidebarContext.Audiogram,
      magicLayoutType: 'AUDIOGRAM',
      visible: true
    }
  ]
};

function isSpeakerLayoutTile(layout: LayoutType): boolean {
  return layout === 'GRID' || layout === 'SPEAKER' || layout === 'FOCUS';
}

export function getClipProcessingState(layout: LayoutType, clip: Clip): boolean {
  if (
    isClipFromRecordingStudio(clip) ||
    isLiveEventRemotionWithPreviewAndAlignedStream(clip) ||
    isClipPreRecordedEvent(clip)
  ) {
    return false;
  }

  return !isClipFromUploads(clip) && getLayoutAvailabilityStatus(layout, clip.layout_status, clip) === 'PROCESSING';
}

/**
 * @deprecated Should be only used for higher states of uploads processing - PROCESSING, PROCESSED, UNAVAILABLE
 */
export function getClipUnavailableState(layout: LayoutType, clip: Clip): [boolean, string, string] {
  if (isClipFromRecordingStudio(clip) || isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    return [false, '', ''];
  }

  if (!isSpeakerLayoutTile(layout)) {
    return [false, '', ''];
  }

  //eslint-disable-next-line
  const { status, reason } = clip.layout_status?.[layout === 'FOCUS' ? 'GRID' : layout];

  if ([ClipLayoutStatus.Failed, ClipLayoutStatus.Unavailable].includes(status)) {
    return [true, CLIP_UNAVAILABLE_MESSAGES[layout]?.[reason] || DEFAULT_REASON, reason];
  }
  return [false, '', reason];
}
